import {
  ArrowDownIcon,
  ArrowUpIcon,
  CaretRightIcon,
  CheckIcon,
  ClockCounterClockwiseIcon,
  XIcon,
} from '@phosphor-icons/react'
import { mappedColors, typography } from '@pixee/design-system'
import { GitBranchIcon } from '@primer/octicons-react'
import { type Row } from '@tanstack/react-table'
import classNames from 'classnames'
import { DateTime } from 'luxon'
import { useRef } from 'react'
import { match, P } from 'ts-pattern'
import {
  SeveritySchema,
  type CompletedTriageOutcome,
  type FindingResponse,
  type FindingResultResponse,
  type FixResultResponse,
  type PatchState,
  type PushResponse,
  type ResultsScaResultResponse,
  type ResultsTriageResultResponse,
  type ScaClassificationResponse,
  type Severity,
  type Tool,
} from '../../utils/api-client/api-schemas.ts'
import {
  getAnalysisIndicatorStatus,
  getFirstRepresentativeFixResult,
  getFirstRepresentativeScaResult,
  getFirstRepresentativeTriageResult,
  getLoweredCvssDelta,
  isRecentlyAnalyzed,
  type AnalysisIndicatorStatus,
} from '../../utils/finding-utils.ts'
import { type EnvironmentData } from '../../utils/hooks/get-environment-data.ts'
import { useIsOverflowing } from '../../utils/hooks/use-is-overflowing.ts'
import { capitalizeFirstLetter } from '../../utils/text-utils.ts'
import { PushStatusBadge, type PolarisStatusView } from '../badges/push-status-badge'
import { SeverityBadge } from '../badges/severity-badge'
import { DefaultButton } from '../buttons/default-button'
import { GhostButton } from '../buttons/ghost-button'
import { OutcomeTag } from '../outcomes/outcome-tag.tsx'
import { type OutcomeType } from '../outcomes/outcome-types.ts'
import { PulsingDot } from '../pulsing-dot.tsx'
import { Spinner } from '../spinner.tsx'
import { SuggestedSeverity } from '../suggested-severity.tsx'
import { ToolIcon } from '../third-party-branding/tool-icon.tsx'
import { Tooltip, TooltipWithHover } from '../tooltip'
import * as styles from './findings-table.css'
import * as expandStyles from './scan-findings-table.css'
import { type RowData, type SubRowData } from './scan-findings-table.hooks'

export const FIX_CELL_TEST_ID = 'fix-cell'

// #region Type Definitions & Utilities

type CellProps = {
  row: Row<RowData>
  onSelect?: (id: string) => unknown
  onOpenFindingTriageDrawer?: (id: string, resultId?: string) => unknown
  onOpenFindingFixDrawer?: (id: string, resultId?: string) => unknown
  areRowsSelectable?: boolean
  integrationName?: string
  /**
   * What Polaris says about each finding whose badge has been reached, keyed by triage result ID.
   *
   * Plain data from above rather than a lookup here: these cells render inside
   * `ScanFindingsTablePure`, so the request belongs to the impure table.
   */
  polarisStatusViews?: Record<string, PolarisStatusView>
  /** Starts the Polaris lookup for one finding. Absent when there is no Polaris state to look up. */
  onRevealPolarisStatus?: (triageResultId: string) => unknown
}

type HeaderProps = {
  environmentData: EnvironmentData
}

const getOutcomeType = ({
  status,
  classification,
}: {
  status?: CompletedTriageOutcome['suggested_status']
  classification?: ScaClassificationResponse
}): OutcomeType | undefined =>
  match({ status, classification })
    .with({ classification: 'not-exploitable' }, () => 'no-fix-necessary-not-exploitable' as const)
    .with({ status: 'wont_fix' }, () => 'no-fix-necessary-wont-fix' as const)
    .with({ status: 'false_positive' }, () => 'no-fix-necessary-false-positive' as const)
    .with({ status: 'true_positive' }, () => 'true-positive' as const)
    .with({ status: 'suspicious' }, () => 'likely-true-positive' as const)
    .with({ status: 'inconclusive' }, () => 'inconclusive' as const)
    .with({ status: 'blocked' }, () => 'blocked' as const)
    .with({ status: 'excluded' }, () => 'excluded' as const)
    .with({ status: 'error' }, () => 'error' as const)
    .with({ classification: 'exploitable' }, () => 'exploitable' as const)
    .with({ classification: 'inconclusive' }, () => 'potentially-exploitable' as const)
    .with({ classification: 'potentially-exploitable' }, () => 'potentially-exploitable' as const)
    .otherwise(() => undefined)

export const hasInProgressRepresentativeAnalysis = (finding: FindingResponse): boolean => {
  const triage = getFirstRepresentativeTriageResult(finding)
  const fix = getFirstRepresentativeFixResult(finding)
  const sca = getFirstRepresentativeScaResult(finding)
  return (
    (triage !== undefined && triage.outcome === undefined) ||
    (fix !== undefined && fix.outcome === undefined) ||
    (sca !== undefined && sca.outcome === undefined)
  )
}

/**
 * Determines if a finding row is clickable (opens drawer on click).
 * Clickable when any analysis result exists (in-progress, completed, or failed).
 * The drawer handles all states: activity stream for in-progress, summary for completed, error for failed.
 * Only truly unanalyzed findings (zero results) are not clickable.
 */
export const isRowClickable = (finding: FindingResponse, areRowsSelectable: boolean): boolean => {
  const triageResult = getFirstRepresentativeTriageResult(finding)
  const scaResult = getFirstRepresentativeScaResult(finding)
  const fixResult = getFirstRepresentativeFixResult(finding)

  const hasAnyResult = triageResult !== undefined || scaResult !== undefined || fixResult !== undefined

  return hasAnyResult && areRowsSelectable
}

const getSuggestedSeverity = (triageResult?: FindingResultResponse): Severity | undefined => {
  if (
    !triageResult ||
    !triageResult.outcome ||
    triageResult.type !== 'triage' ||
    triageResult.outcome.type !== 'completed-triage'
  ) {
    return undefined
  }

  return SeveritySchema.safeParse(triageResult.outcome.suggested_severity).data
}

const isDismissiveClassification = (triageResult?: FindingResultResponse): boolean => {
  if (triageResult?.outcome?.type !== 'completed-triage') return false
  const status = triageResult.outcome.suggested_status
  return status === 'false_positive' || status === 'wont_fix'
}

/**
 * Get timestamp for the analysis result.
 * Uses the completed timestamp from triage, fix, or SCA result, whichever is available.
 */
const getTimestamp = (subRow: SubRowData): string | null => {
  return match(subRow)
    .with({ type: 'sast' }, sast => sast.triage?.completed ?? sast.fix?.completed ?? null)
    .with({ type: 'sca' }, sca => sca.sca.completed ?? null)
    .exhaustive()
}

/**
 * Get relative time for the analysis result.
 * Uses the completed timestamp from triage, fix, or SCA result, whichever is available.
 */
const getRelativeTime = (subRow: SubRowData): string | null => {
  const timestamp = getTimestamp(subRow)
  if (!timestamp) return null
  return DateTime.fromISO(timestamp).toRelative()
}

/**
 * Determines if this sub-row represents the first chronological analysis.
 * Compares timestamps across all sub-rows (representative + non-representative) to find the earliest.
 */
const isFirstChronologically = (currentRow: Row<RowData>, currentSubRowData: SubRowData): boolean => {
  const parentRow = currentRow.getParentRow()
  if (!parentRow) return false

  const currentTimestamp = getTimestamp(currentSubRowData)
  if (!currentTimestamp) return false

  // Get all sub-rows (representative + non-representative)
  const allSubRows = parentRow.subRows

  // Find the earliest timestamp among all sub-rows
  let earliestTimestamp = currentTimestamp
  for (const subRow of allSubRows) {
    if (subRow.original.type === 'outer') continue

    const timestamp = getTimestamp(subRow.original.data)
    if (timestamp && timestamp < earliestTimestamp) {
      earliestTimestamp = timestamp
    }
  }

  // This row is first if its timestamp equals the earliest
  return currentTimestamp === earliestTimestamp
}

/**
 * Determines if this sub-row has the most recent timestamp among ALL sub-rows.
 * Returns true if no other sub-row has a more recent timestamp.
 */
const isLatest = (currentRow: Row<RowData>, currentSubRowData: SubRowData): boolean => {
  const parentRow = currentRow.getParentRow()
  if (!parentRow) return false

  const currentTimestamp = getTimestamp(currentSubRowData)
  if (!currentTimestamp) return false

  // Get all sub-rows (representative + non-representative)
  const allSubRows = parentRow.subRows

  // Check if any other sub-row has a more recent timestamp
  return !allSubRows
    .map(({ original }) => original)
    .filter(original => original.type !== 'outer')
    .map(original => getTimestamp(original.data))
    .some(timestamp => timestamp && timestamp > currentTimestamp)
}

/**
 * Get a descriptive label for the analysis.
 * - First analysis: "Initial analysis"
 * - Subsequent analyses: "Manual retry"
 */
const getAnalysisTypeLabel = (isFirst: boolean): string => {
  return isFirst ? 'Initial analysis' : 'Manual retry'
}

export const getSuggestedStatusLabel = (status?: CompletedTriageOutcome['suggested_status']) =>
  match(status)
    .with('wont_fix', () => "Won't fix")
    .with('false_positive', () => 'False positive')
    .with('true_positive', () => 'True positive')
    .with('suspicious', () => 'Likely true positive')
    .with('inconclusive', () => 'Inconclusive')
    .with('not_triaged', () => 'Not triaged')
    .with('blocked', () => 'Blocked')
    .with('excluded', () => 'Excluded')
    .with('error', () => 'Error')
    .with(undefined, () => 'Inconclusive')
    .exhaustive()

export const getScaClassificationLabel = (classification?: ScaClassificationResponse) =>
  match(classification)
    .with('exploitable', () => 'Exploitable')
    .with('not-exploitable', () => 'Not exploitable')
    .with('inconclusive', 'potentially-exploitable', () => 'Potentially exploitable')
    .with(undefined, () => 'Potentially exploitable')
    .exhaustive()

// #endregion

// #region Wrapper Components

export const openSubRowDrawer = (
  subRowData: SubRowData,
  onOpenFindingTriageDrawer: (findingId: string, resultId?: string) => unknown,
  onOpenFindingFixDrawer: (findingId: string, resultId?: string) => unknown
): void => {
  const hasTriageOrSca = match(subRowData)
    .with({ type: 'sast' }, sast => !!sast.triage)
    .with({ type: 'sca' }, () => true)
    .exhaustive()

  if (hasTriageOrSca) {
    const resultId = match(subRowData)
      .with({ type: 'sast' }, sast => sast.triage?.id)
      .with({ type: 'sca' }, sca => sca.sca?.id)
      .exhaustive()
    onOpenFindingTriageDrawer(subRowData.findingId, resultId)
  } else {
    const resultId = match(subRowData)
      .with({ type: 'sast' }, sast => sast.fix?.id)
      .with({ type: 'sca' }, () => undefined)
      .exhaustive()
    onOpenFindingFixDrawer(subRowData.findingId, resultId)
  }
}

// #endregion

// #region Cell Components

type InProgressCellIndicatorProps = {
  status: AnalysisIndicatorStatus
}

/**
 * Renders in-progress indicator for use inside table cells (triage/fix columns).
 * Shows glowing dot only for first-analysis-in-progress (when no completed result exists).
 * For reanalysis, the cells show the completed representative values and spinner is at row end.
 */
const InProgressCellIndicator: React.FC<InProgressCellIndicatorProps> = ({ status }) =>
  match(status)
    .with('first-analysis-in-progress', () => (
      <span
        className={expandStyles.glowingDot}
        role="status"
        aria-label="Analysis in progress"
        data-testid="analysis-in-progress-indicator"
      />
    ))
    .otherwise(() => null)

type AnalysisStatusIndicatorProps = {
  status: AnalysisIndicatorStatus
}

/**
 * Renders indicator in the expansion column at end of row.
 * Shows spinner for reanalysis-in-progress, dot for representative-not-latest.
 * First analysis in progress only shows glowing dot in cells, not here.
 */
const AnalysisStatusIndicator: React.FC<AnalysisStatusIndicatorProps> = ({ status }) =>
  match(status)
    .with('reanalysis-in-progress', () => (
      <span data-testid="reanalysis-indicator" className={expandStyles.indicatorWrapper}>
        <Spinner size="20" variant="updating" label="Re-analysis in progress" />
      </span>
    ))
    .with('representative-not-latest', () => (
      <span
        className={expandStyles.indicatorWrapper}
        role="status"
        aria-label="Newer analysis available"
        data-testid="not-latest-indicator"
      >
        <span className={expandStyles.notificationDot} />
      </span>
    ))
    .otherwise(() => null)

type ExpansionToggleCellProps = {
  row: Row<RowData>
}

export const ExpansionToggleCell: React.FC<ExpansionToggleCellProps> = ({ row }) => {
  if (row.original.type !== 'outer') {
    return null
  }

  const analysisStatus = getAnalysisIndicatorStatus(row.original.data)
  const canExpand = row.getCanExpand()
  const isExpanded = row.getIsExpanded()

  if (!canExpand && !analysisStatus) {
    return null
  }

  return (
    <div className={expandStyles.analysisStatusContainer}>
      <AnalysisStatusIndicator status={analysisStatus} />
      {canExpand && (
        <div
          className={expandStyles.expandToggle}
          onClick={e => {
            e.stopPropagation()
            row.toggleExpanded()
          }}
          role="button"
          aria-expanded={isExpanded}
          aria-label={isExpanded ? 'Collapse row' : 'Expand row'}
        >
          <CaretRightIcon className={expandStyles.caretIcon} size={16} weight="light" />
        </div>
      )}
    </div>
  )
}

export const SeverityCell: React.FC<CellProps> = ({ row }) =>
  match(row.original)
    .with({ type: 'outer' }, outerRow => (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <SeverityBadge severity={outerRow.data.severity} />
      </div>
    ))
    .with({ type: 'representative' }, () => null)
    .with({ type: 'non-representative' }, subRow => (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <SeverityBadge severity={subRow.data.parentSeverity} />
      </div>
    ))
    .exhaustive()

const SEVERITY_INDICATOR_SIZE = 16

const getSeverityComparisonIndicatorComponent = (
  suggestedSeverity: Severity | undefined,
  toolSeverity: Severity | null | undefined
): React.ReactNode => {
  if (!suggestedSeverity || !toolSeverity) {
    return <div style={{ width: SEVERITY_INDICATOR_SIZE, height: SEVERITY_INDICATOR_SIZE }} />
  }

  return match(Math.sign(suggestedSeverity.rank - toolSeverity.rank))
    .with(1, () => (
      <ArrowUpIcon
        aria-label="Severity increased"
        size={SEVERITY_INDICATOR_SIZE}
        weight="light"
        style={{ color: mappedColors.secondary.haze }}
      />
    ))
    .with(-1, () => (
      <ArrowDownIcon
        aria-label="Severity decreased"
        size={SEVERITY_INDICATOR_SIZE}
        weight="light"
        style={{ color: mappedColors.neutral.neutral30 }}
      />
    ))
    .otherwise(() => (
      <CheckIcon
        aria-label="Severity unchanged"
        size={SEVERITY_INDICATOR_SIZE}
        weight="regular"
        style={{ color: mappedColors.primary.nebula }}
      />
    ))
}

type PixeeSeverityContentProps = {
  displaySeverity: Severity | null | undefined
  severityIndicatorComponent: React.ReactNode
  isActive: boolean
}

const PixeeSeverityContent: React.FC<PixeeSeverityContentProps> = ({
  displaySeverity,
  severityIndicatorComponent,
  isActive,
}) => (
  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', gap: '4px' }}>
    <div style={{ opacity: isActive ? 1 : 0.5 }}>
      <SeverityBadge severity={displaySeverity} />
    </div>
    <div
      style={{
        width: SEVERITY_INDICATOR_SIZE,
        height: SEVERITY_INDICATOR_SIZE,
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {severityIndicatorComponent}
    </div>
  </div>
)

export const PixeeSeverityCell: React.FC<CellProps> = ({ row }) =>
  match(row.original)
    .with({ type: 'outer' }, outerRow => {
      const triageResult = getFirstRepresentativeTriageResult(outerRow.data)
      const suggestedSeverity = getSuggestedSeverity(triageResult)
      const toolSeverity = outerRow.data.severity
      const displaySeverity = suggestedSeverity ?? toolSeverity
      const isDismissive = isDismissiveClassification(triageResult)
      const isActive = suggestedSeverity !== undefined && !isDismissive
      const severityIndicatorComponent = !isDismissive
        ? getSeverityComparisonIndicatorComponent(suggestedSeverity, toolSeverity)
        : null

      return (
        <PixeeSeverityContent
          displaySeverity={displaySeverity}
          severityIndicatorComponent={severityIndicatorComponent}
          isActive={isActive}
        />
      )
    })
    .with({ type: 'representative' }, () => null)
    .with({ type: 'non-representative' }, subRow => {
      const triageResult = match(subRow.data)
        .with({ type: 'sast' }, sast => sast.triage)
        .with({ type: 'sca' }, () => undefined)
        .exhaustive()
      const suggestedSeverity = getSuggestedSeverity(triageResult)
      const toolSeverity = subRow.data.parentSeverity
      const displaySeverity = suggestedSeverity ?? toolSeverity
      const isDismissive = isDismissiveClassification(triageResult)
      const isActive = suggestedSeverity !== undefined && !isDismissive
      const severityIndicatorComponent = !isDismissive
        ? getSeverityComparisonIndicatorComponent(suggestedSeverity, toolSeverity)
        : null

      return (
        <PixeeSeverityContent
          displaySeverity={displaySeverity}
          severityIndicatorComponent={severityIndicatorComponent}
          isActive={isActive}
        />
      )
    })
    .exhaustive()

const formatSeverityLabel = (severity: Severity | null | undefined): string => {
  if (!severity?.label) return '-'
  return capitalizeFirstLetter(severity.label)
}

export const ToolSeverityCell: React.FC<CellProps> = ({ row }) =>
  match(row.original)
    .with({ type: 'outer' }, outerRow => {
      const triageResult = getFirstRepresentativeTriageResult(outerRow.data)
      const hasSuccessfulTriage = triageResult?.outcome?.type === 'completed-triage'
      const textColor = hasSuccessfulTriage ? mappedColors.neutral.neutral30 : mappedColors.neutral.halo

      return (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
          <span className={typography.small} style={{ color: textColor }}>
            {formatSeverityLabel(outerRow.data.severity)}
          </span>
        </div>
      )
    })
    .with({ type: 'representative' }, () => null)
    .with({ type: 'non-representative' }, subRow => {
      const triageResult = match(subRow.data)
        .with({ type: 'sast' }, sast => sast.triage)
        .with({ type: 'sca' }, () => undefined)
        .exhaustive()
      const hasSuccessfulTriage = triageResult?.outcome?.type === 'completed-triage'
      const textColor = hasSuccessfulTriage ? mappedColors.neutral.neutral30 : mappedColors.neutral.halo

      return (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
          <span className={typography.small} style={{ color: textColor }}>
            {formatSeverityLabel(subRow.data.parentSeverity)}
          </span>
        </div>
      )
    })
    .exhaustive()

export const CvssScoreHeader: React.FC<HeaderProps> = ({ environmentData }) => (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
    <img src={environmentData.logoIconHref} height={16} style={{ marginRight: '8px' }} alt="" />
    <span className={styles.header} style={{ margin: 0, padding: 0, width: 'fit-content' }}>
      CVSS
    </span>
  </div>
)

type CvssScoreContentProps = {
  effectiveCvss: Severity | undefined
  baseCvss: Severity | undefined
  hasSuccessfulTriage: boolean
}

/**
 * Renders the contextual (effective) CVSS for a finding. When Pixee has lowered the score from its
 * base value the value is accented and a tooltip shows the delta (see `getLoweredCvssDelta`).
 *
 * TODO(ISS-8892): the full "why" tooltip (rationale + per-metric derived_metrics) comes from the
 * finding-detail endpoint and is a fast-follow; the list fields only carry base → contextual here.
 */
const CvssScoreContent: React.FC<CvssScoreContentProps> = ({ effectiveCvss, baseCvss, hasSuccessfulTriage }) => {
  const textColor = hasSuccessfulTriage ? mappedColors.neutral.neutral30 : mappedColors.neutral.halo
  const effectiveLabel = effectiveCvss?.label ?? '-'
  const loweredDelta = getLoweredCvssDelta(baseCvss, effectiveCvss)
  const isLoweredByPixee = loweredDelta !== undefined

  const valueSpan = (
    <span
      className={classNames({ [typography.small]: !isLoweredByPixee, [expandStyles.cvssLowered]: isLoweredByPixee })}
      style={isLoweredByPixee ? undefined : { color: textColor }}
    >
      {effectiveLabel}
    </span>
  )

  return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
      {loweredDelta !== undefined ? (
        // TODO(ISS-8892): the finding's real rationale ("why") lives on the finding-detail
        // endpoint, not the findings list — add it as a description line here as a fast-follow.
        // For now the tooltip shows only the base → contextual delta.
        <TooltipWithHover side="top" trigger={valueSpan}>
          {`${loweredDelta.base} → ${loweredDelta.contextual}`}
        </TooltipWithHover>
      ) : (
        valueSpan
      )}
    </div>
  )
}

export const CvssScoreCell: React.FC<CellProps> = ({ row }) =>
  match(row.original)
    .with({ type: 'outer' }, outerRow => {
      const triageResult = getFirstRepresentativeTriageResult(outerRow.data)
      const hasSuccessfulTriage = triageResult?.outcome?.type === 'completed-triage'

      return (
        <CvssScoreContent
          effectiveCvss={outerRow.data.effective_cvss}
          baseCvss={outerRow.data.base_cvss}
          hasSuccessfulTriage={hasSuccessfulTriage}
        />
      )
    })
    .with({ type: 'representative' }, () => null)
    .with({ type: 'non-representative' }, subRow => {
      const triageResult = match(subRow.data)
        .with({ type: 'sast' }, sast => sast.triage)
        .with({ type: 'sca' }, () => undefined)
        .exhaustive()
      const hasSuccessfulTriage = triageResult?.outcome?.type === 'completed-triage'

      return (
        <CvssScoreContent
          effectiveCvss={subRow.data.parentEffectiveCvss}
          baseCvss={subRow.data.parentBaseCvss}
          hasSuccessfulTriage={hasSuccessfulTriage}
        />
      )
    })
    .exhaustive()

export const ScaClassificationCell: React.FC<CellProps> = ({ row, onOpenFindingTriageDrawer = noop }) =>
  match(row.original)
    .with({ type: 'outer' }, outerRow => {
      const scaResult = getFirstRepresentativeScaResult(outerRow.data)
      const isInProgress = !!scaResult && !scaResult.outcome
      const onDetailsClick = () => onOpenFindingTriageDrawer(outerRow.data.id)

      return (
        <div
          style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}
          aria-busy={isInProgress || undefined}
        >
          <ScaClassificationCellContent scaResult={scaResult} onDetailsClick={onDetailsClick} />
        </div>
      )
    })
    .with({ type: 'representative' }, () => null)
    .with({ type: 'non-representative' }, subRow => {
      const scaResult = match(subRow.data)
        .with({ type: 'sast' }, () => undefined)
        .with({ type: 'sca' }, sca => sca.sca)
        .exhaustive()
      const isInProgress = !!scaResult && !scaResult.outcome
      const onDetailsClick = () => onOpenFindingTriageDrawer(subRow.data.findingId, scaResult?.id)

      return (
        <div
          style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}
          aria-busy={isInProgress || undefined}
        >
          <ScaClassificationCellContent scaResult={scaResult} onDetailsClick={onDetailsClick} />
        </div>
      )
    })
    .exhaustive()

const FindingRuleWithId: React.FC<{ rule: string; id: string }> = ({ rule, id }) => {
  const rowRef = useRef<HTMLParagraphElement>(null)
  const isOverflowing = useIsOverflowing(rowRef, [rule, id])

  return (
    <TooltipWithHover
      trigger={
        <p ref={rowRef} className={styles.findingRule}>
          {rule}
          <span className={styles.findingRuleSeparator}>|</span>
          {id}
        </p>
      }
      side="bottom"
      // Always render the tooltip wrapper so the <p> stays in a stable tree position.
      // Conditionally wrapping would cause React to unmount/remount it on resize,
      // detaching the ref and breaking useIsOverflowing after a wide→narrow→wide→narrow cycle.
      // `undefined` lets Radix handle hover normally; `false` forces the tooltip closed.
      open={isOverflowing ? undefined : false}
    >
      {id}
    </TooltipWithHover>
  )
}

export const TitleCell: React.FC<CellProps> = ({ row, areRowsSelectable = true }) =>
  match(row.original)
    .with({ type: 'outer' }, outerRow => {
      const isSelectable = isRowClickable(outerRow.data, areRowsSelectable)
      const isNew = isRecentlyAnalyzed(outerRow.data)

      return (
        <>
          <div className={styles.findingTitle[isSelectable ? 'triageable' : 'nonTriageable']}>
            <span className={styles.findingTitleText} data-contains-ellipsized-content>
              {outerRow.data.title ?? 'No title'}
            </span>
            {isNew && (
              <span
                className={expandStyles.newlyAnalyzedDot}
                role="status"
                aria-label="Recently analyzed"
                data-testid="newly-analyzed-indicator"
              />
            )}
          </div>
          <FindingRuleWithId rule={outerRow.data.rule} id={outerRow.data.id} />
        </>
      )
    })
    .with({ type: 'representative' }, subRow => {
      const isLatestRow = isLatest(row, subRow.data)
      const isFirst = isFirstChronologically(row, subRow.data)
      const typeLabel = getAnalysisTypeLabel(isFirst)
      const relativeTime = getRelativeTime(subRow.data)

      return (
        <div className={expandStyles.representativeResultRow}>
          <span className={expandStyles.analysisTitle}>{typeLabel}</span>
          {relativeTime && (
            <span className={expandStyles.timestamp}>
              {relativeTime}
              {!isLatestRow && <ClockCounterClockwiseIcon size={12} weight="light" />}
            </span>
          )}
          {isLatestRow && <span className={expandStyles.latestBadge}>LATEST</span>}
        </div>
      )
    })
    .with({ type: 'non-representative' }, subRow => {
      const isLatestRow = isLatest(row, subRow.data)
      const isFirst = isFirstChronologically(row, subRow.data)

      return <MetadataCell subRowData={subRow.data} isLatest={isLatestRow} isFirst={isFirst} />
    })
    .exhaustive()

export const SuggestedStatusCell: React.FC<CellProps> = ({ row }) =>
  match(row.original)
    .with({ type: 'outer' }, outerRow => {
      const triageResult = getFirstRepresentativeTriageResult(outerRow.data)
      return <TriageClassificationCellContent triageResult={triageResult} />
    })
    .with({ type: 'representative' }, () => null)
    .with({ type: 'non-representative' }, subRow => {
      const triageResult = match(subRow.data)
        .with({ type: 'sast' }, sast => sast.triage)
        .with({ type: 'sca' }, () => undefined)
        .exhaustive()

      return <TriageClassificationCellContent triageResult={triageResult} />
    })
    .exhaustive()

export const SeverityUpdateCell: React.FC<CellProps> = ({ row }) =>
  match(row.original)
    .with({ type: 'outer' }, outerRow => {
      const triageResult = getFirstRepresentativeTriageResult(outerRow.data)
      const suggestedSeverity = getSuggestedSeverity(triageResult)
      return <SeverityUpdateCellContent severity={outerRow.data.severity} suggestedSeverity={suggestedSeverity} />
    })
    .with({ type: 'representative' }, () => null)
    .with({ type: 'non-representative' }, subRow => {
      const triageResult = match(subRow.data)
        .with({ type: 'sast' }, sast => sast.triage)
        .with({ type: 'sca' }, () => undefined)
        .exhaustive()

      const suggestedSeverity = getSuggestedSeverity(triageResult)

      return (
        <SeverityUpdateCellContent
          severity={subRow.data.parentSeverity ?? undefined}
          suggestedSeverity={suggestedSeverity}
        />
      )
    })
    .exhaustive()

type FixCellOuterRowProps = {
  outerRow: { data: FindingResponse }
  onSelect: (id: string) => unknown
  onOpenFindingFixDrawer?: (id: string, resultId?: string) => unknown
}

const FixCellOuterRow: React.FC<FixCellOuterRowProps> = ({ outerRow, onSelect, onOpenFindingFixDrawer }) => {
  const fixResult = getFirstRepresentativeFixResult(outerRow.data)
  const triageResult = getFirstRepresentativeTriageResult(outerRow.data)
  const scaResult = getFirstRepresentativeScaResult(outerRow.data)
  const scaClassification = scaResult?.outcome?.type === 'completed-sca' ? scaResult.outcome.classification : undefined
  const suggestedStatus =
    triageResult?.outcome?.type === 'completed-triage' ? (triageResult.outcome.suggested_status ?? null) : null
  const latestPatchState = fixResult?._embedded?.['latest-patch']?._embedded?.items?.[0]?.state
  const analysisStatus = getAnalysisIndicatorStatus(outerRow.data)
  const fixAnalysisStatus: AnalysisIndicatorStatus =
    fixResult && !fixResult.outcome ? 'first-analysis-in-progress' : analysisStatus
  const isInProgress = fixAnalysisStatus === 'first-analysis-in-progress'

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    onSelect(outerRow.data.id)
  }

  const onDetailsClick = onOpenFindingFixDrawer ? () => onOpenFindingFixDrawer(outerRow.data.id) : undefined

  return (
    <div
      style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}
      aria-busy={isInProgress || undefined}
    >
      <FixCellContent
        fixResult={fixResult}
        triageResult={triageResult}
        suggestedStatus={suggestedStatus}
        latestPatchState={latestPatchState}
        onViewClick={handleClick}
        analysisStatus={fixAnalysisStatus}
        scaClassification={scaClassification}
        onDetailsClick={onDetailsClick}
      />
    </div>
  )
}

type FixCellNonRepresentativeRowProps = {
  subRow: { type: 'non-representative'; data: SubRowData }
  onOpenFindingFixDrawer: (id: string, resultId?: string) => unknown
}

const FixCellNonRepresentativeRow: React.FC<FixCellNonRepresentativeRowProps> = ({
  subRow,
  onOpenFindingFixDrawer,
}) => {
  const fixResult = match(subRow.data)
    .with({ type: 'sast' }, sast => sast.fix)
    .with({ type: 'sca' }, sca => sca.fix)
    .exhaustive()

  const triageResult = match(subRow.data)
    .with({ type: 'sast' }, sast => sast.triage)
    .with({ type: 'sca' }, () => undefined)
    .exhaustive()

  const scaResult = match(subRow.data)
    .with({ type: 'sast' }, () => undefined)
    .with({ type: 'sca' }, sca => sca.sca)
    .exhaustive()

  const scaClassification = scaResult?.outcome?.type === 'completed-sca' ? scaResult.outcome.classification : undefined
  const suggestedStatus =
    triageResult?.outcome?.type === 'completed-triage' ? (triageResult.outcome.suggested_status ?? null) : null
  const latestPatchState = fixResult?._embedded?.['latest-patch']?._embedded?.items?.[0]?.state
  const subRowAnalysisStatus: AnalysisIndicatorStatus =
    fixResult && !fixResult.outcome ? 'first-analysis-in-progress' : null
  const isInProgress = subRowAnalysisStatus === 'first-analysis-in-progress'

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    onOpenFindingFixDrawer(subRow.data.findingId, fixResult?.id)
  }

  const onDetailsClick = () => onOpenFindingFixDrawer(subRow.data.findingId, fixResult?.id)

  return (
    <div
      style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}
      aria-busy={isInProgress || undefined}
    >
      <FixCellContent
        fixResult={fixResult}
        triageResult={triageResult}
        suggestedStatus={suggestedStatus}
        latestPatchState={latestPatchState}
        onViewClick={handleClick}
        analysisStatus={subRowAnalysisStatus}
        scaClassification={scaClassification}
        onDetailsClick={onDetailsClick}
      />
    </div>
  )
}

const noop = () => {}

export const FixCell: React.FC<CellProps> = ({ row, onSelect = noop, onOpenFindingFixDrawer = noop }) =>
  match(row.original)
    .with({ type: 'outer' }, outerRow => (
      <FixCellOuterRow outerRow={outerRow} onSelect={onSelect} onOpenFindingFixDrawer={onOpenFindingFixDrawer} />
    ))
    .with({ type: 'representative' }, () => null)
    .with({ type: 'non-representative' }, subRow => (
      <FixCellNonRepresentativeRow subRow={subRow} onOpenFindingFixDrawer={onOpenFindingFixDrawer} />
    ))
    .exhaustive()

/**
 * Builds the badge's reveal handler, or nothing when there is no Polaris status to ask for.
 *
 * Whether the badge is interactive is decided by the presence of this handler, so a finding with no
 * triage result and a non-Polaris integration both resolve to `undefined` here rather than to a
 * focusable badge whose tooltip never fills in.
 */
const buildPolarisReveal = ({
  triageResult,
  onRevealPolarisStatus,
}: {
  triageResult?: { id: string }
  onRevealPolarisStatus?: (triageResultId: string) => unknown
}) => {
  if (!triageResult || !onRevealPolarisStatus) {
    return undefined
  }
  return () => onRevealPolarisStatus(triageResult.id)
}

export const ClassificationCell: React.FC<CellProps> = ({
  row,
  onOpenFindingTriageDrawer = noop,
  integrationName,
  polarisStatusViews,
  onRevealPolarisStatus,
}) =>
  match(row.original)
    .with({ type: 'outer' }, outerRow => {
      const triageResult = getFirstRepresentativeTriageResult(outerRow.data)
      const analysisStatus = getAnalysisIndicatorStatus(outerRow.data)
      const onDetailsClick = () => onOpenFindingTriageDrawer(outerRow.data.id)
      const isInProgress = analysisStatus === 'first-analysis-in-progress'
      // Prefer push state on the triage result itself (per-result from backend); fall back to
      // the finding-level latest only when it targets the representative triage.
      const findingLatest = outerRow.data.push_state
      const pushState =
        triageResult?.latest_push_state ??
        (triageResult && findingLatest?.triage_result_ids.includes(triageResult.id) ? findingLatest : undefined)
      const allResultItems = outerRow.data._embedded?.results?._embedded.items ?? []
      const pushedTriageResult = findingLatest
        ? allResultItems.find(
            (item): item is ResultsTriageResultResponse =>
              item.type === 'triage' && findingLatest.triage_result_ids.includes(item.id)
          )
        : undefined
      const currentSuggestedStatus =
        triageResult?.outcome?.type === 'completed-triage' ? triageResult.outcome.suggested_status : undefined
      const pushedSuggestedStatus =
        pushedTriageResult?.outcome?.type === 'completed-triage'
          ? pushedTriageResult.outcome.suggested_status
          : undefined
      const hasStaleClassification =
        findingLatest?.status === 'SUCCESS' &&
        !pushState &&
        pushedTriageResult !== undefined &&
        currentSuggestedStatus !== pushedSuggestedStatus

      return (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
          }}
          aria-busy={isInProgress || undefined}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <TriageClassificationCellContent
              triageResult={triageResult}
              analysisStatus={analysisStatus}
              onDetailsClick={onDetailsClick}
            />
            {pushState && (
              <PushStatusBadge
                pushState={pushState}
                integrationName={integrationName}
                polarisStatusView={triageResult ? polarisStatusViews?.[triageResult.id] : undefined}
                onReveal={buildPolarisReveal({ triageResult, onRevealPolarisStatus })}
              />
            )}
            {hasStaleClassification && (
              <TooltipWithHover
                side="top"
                maxWidth={260}
                trigger={
                  <span
                    role="img"
                    aria-label="Classification changed since last push"
                    style={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      backgroundColor: mappedColors.outcomeBanner.successIcon,
                      display: 'inline-block',
                      flexShrink: 0,
                      cursor: 'default',
                    }}
                  />
                }
              >
                {`Classification was previously pushed${integrationName ? ` to ${integrationName}` : ''} and has changed with additional analysis`}
              </TooltipWithHover>
            )}
          </div>
        </div>
      )
    })
    .with({ type: 'representative' }, () => null)
    .with({ type: 'non-representative' }, subRow => {
      const triageResult = match(subRow.data)
        .with({ type: 'sast' }, sast => sast.triage)
        .with({ type: 'sca' }, () => undefined)
        .exhaustive()

      const subRowAnalysisStatus: AnalysisIndicatorStatus =
        triageResult && !triageResult.outcome ? 'first-analysis-in-progress' : null
      const isInProgress = subRowAnalysisStatus === 'first-analysis-in-progress'
      const onDetailsClick = () => onOpenFindingTriageDrawer(subRow.data.findingId, triageResult?.id)

      return (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
          }}
          aria-busy={isInProgress || undefined}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <TriageClassificationCellContent
              triageResult={triageResult}
              analysisStatus={subRowAnalysisStatus}
              onDetailsClick={onDetailsClick}
            />
            {subRow.data.pushState && (
              <PushStatusBadge
                pushState={subRow.data.pushState}
                integrationName={integrationName}
                polarisStatusView={triageResult ? polarisStatusViews?.[triageResult.id] : undefined}
                onReveal={buildPolarisReveal({ triageResult, onRevealPolarisStatus })}
              />
            )}
          </div>
        </div>
      )
    })
    .exhaustive()

// #endregion

// #region Content Components

export type FixCellContentProps = {
  fixResult: FixResultResponse | undefined
  triageResult: ResultsTriageResultResponse | undefined
  suggestedStatus: CompletedTriageOutcome['suggested_status'] | null
  latestPatchState: PatchState | undefined
  onViewClick: (e: React.MouseEvent) => void
  analysisStatus?: AnalysisIndicatorStatus
  scaClassification?: ScaClassificationResponse
  /** Opens the fix analysis drawer when the popover Details link is clicked. */
  onDetailsClick?: () => unknown
}

/**
 * Shared content component for fix cell rendering.
 * Used by both outer row FixCell and inner row FixActionCell.
 *
 * Handles all fix outcome types plus triage fallback cases.
 * When a patch exists, displays patch state (in_progress, completed_success, completed_failed).
 * Shows in-progress indicator when analysis is in progress.
 */
export const FixCellContent: React.FC<FixCellContentProps> = ({
  fixResult,
  triageResult,
  suggestedStatus,
  latestPatchState,
  onViewClick,
  analysisStatus,
  scaClassification,
  onDetailsClick,
}) =>
  match({ fixResult, triageResult, suggestedStatus, latestPatchState, analysisStatus, scaClassification })
    .with({ suggestedStatus: P.union('wont_fix', 'false_positive') }, () => (
      <OutcomeTag outcomeType="no-fix-necessary" />
    ))
    .with({ fixResult: { outcome: { type: 'completed-fix' } }, latestPatchState: 'in-progress' }, () => (
      <GhostButton type="secondary" className={styles.fixCellButton} onClick={onViewClick} ariaLabel="View">
        View
        <output aria-label="Patch in progress" className={styles.patchStatus}>
          <PulsingDot variant="in-progress" className={styles.patchInProgressIcon} />
        </output>
      </GhostButton>
    ))
    .with(
      {
        fixResult: { outcome: { type: 'completed-fix' } },
        latestPatchState: P.union('completed-success', 'completed-fixes-overcome-by-events'),
      },
      () => (
        <GhostButton type="secondary" className={styles.fixCellButton} onClick={onViewClick} ariaLabel="View">
          View
          <output aria-label="Patch completed" className={styles.patchStatus}>
            <GitBranchIcon className={styles.patchCompletedIcon} />
          </output>
        </GhostButton>
      )
    )
    .with({ fixResult: { outcome: { type: 'completed-fix' } }, latestPatchState: 'completed-failed' }, () => (
      <GhostButton type="secondary" className={styles.fixCellButton} onClick={onViewClick} ariaLabel="View">
        View
        <output aria-label="Patch completed with errors" className={styles.patchStatus}>
          <XIcon className={styles.patchCompletedWithErrorsIcon} size={12} weight="light" />
        </output>
      </GhostButton>
    ))
    .with({ fixResult: { outcome: { type: 'completed-fix' } } }, () => (
      <DefaultButton buttonType="secondary" size="small" onClick={onViewClick}>
        Preview
      </DefaultButton>
    ))
    .with({ scaClassification: 'not-exploitable' }, () => <OutcomeTag outcomeType="no-fix-necessary" />)
    .with({ fixResult: { outcome: { type: 'failed-fix' } } }, ({ fixResult }) => (
      <OutcomeTag
        outcomeType="error"
        errorMessage={fixResult.outcome.problem.message || undefined}
        onDetailsClick={onDetailsClick}
      />
    ))
    .with({ fixResult: { outcome: { type: 'blocked-fix' } } }, () => (
      <OutcomeTag outcomeType="blocked" onDetailsClick={onDetailsClick} />
    ))
    .with({ fixResult: { outcome: { type: 'excluded-fix' } } }, () => (
      <OutcomeTag outcomeType="excluded" onDetailsClick={onDetailsClick} />
    ))
    .with({ fixResult: { outcome: { type: 'no-recommendations-fix' } } }, () => (
      <OutcomeTag outcomeType="no-fix-necessary" />
    ))
    .with({ triageResult: { outcome: { type: 'failed-triage' } } }, ({ triageResult }) => (
      <OutcomeTag
        outcomeType="error"
        errorMessage={triageResult.outcome.problem.message || undefined}
        onDetailsClick={onDetailsClick}
      />
    ))
    .with(
      { analysisStatus: P.union('first-analysis-in-progress', 'reanalysis-in-progress') },
      ({ analysisStatus: status }) => <InProgressCellIndicator status={status} />
    )
    .with({ fixResult: { outcome: P.optional(undefined) } }, () => '-')
    .otherwise(() => <span className={styles.suggestedStatusCell({ status: 'null' })}>-</span>)

export type TriageClassificationCellContentProps = {
  triageResult: ResultsTriageResultResponse | undefined
  analysisStatus?: AnalysisIndicatorStatus
  /** Opens the triage analysis drawer when the popover Details link is clicked. */
  onDetailsClick?: () => unknown
  pushState?: PushResponse | null
}

/**
 * Shared content component for triage classification cell rendering.
 * Used by both outer row SuggestedStatusCell and inner row ClassificationCell.
 * Shows in-progress indicator when analysis is in progress.
 */
export const TriageClassificationCellContent: React.FC<TriageClassificationCellContentProps> = ({
  triageResult,
  analysisStatus,
  onDetailsClick,
}) =>
  match({ triageResult, analysisStatus })
    .with({ triageResult: { outcome: { type: 'completed-triage' } } }, ({ triageResult: result }) => {
      const outcomeType = getOutcomeType({ status: result.outcome.suggested_status })
      if (outcomeType === undefined) {
        return <span className={styles.suggestedStatusCell({ status: 'null' })}>-</span>
      }
      return <OutcomeTag outcomeType={outcomeType} onDetailsClick={onDetailsClick} />
    })
    .with({ triageResult: { outcome: { type: 'blocked-triage' } } }, () => (
      <OutcomeTag outcomeType="blocked" onDetailsClick={onDetailsClick} />
    ))
    .with({ triageResult: { outcome: { type: 'excluded-triage' } } }, () => (
      <OutcomeTag outcomeType="excluded" onDetailsClick={onDetailsClick} />
    ))
    .with({ triageResult: { outcome: { type: 'failed-triage' } } }, ({ triageResult }) => (
      <OutcomeTag
        outcomeType="error"
        errorMessage={triageResult.outcome.problem.message || undefined}
        onDetailsClick={onDetailsClick}
      />
    ))
    .with(
      { analysisStatus: P.union('first-analysis-in-progress', 'reanalysis-in-progress') },
      ({ analysisStatus: status }) => <InProgressCellIndicator status={status} />
    )
    .otherwise(() => <span className={styles.suggestedStatusCell({ status: 'null' })}>-</span>)

export type SeverityUpdateCellContentProps = {
  severity: Severity | null | undefined
  suggestedSeverity: Severity | undefined
}

/**
 * Shared content component for severity update cell rendering.
 * Used by both outer row SeverityUpdateCell and inner row SeverityUpdateCell.
 */
export const SeverityUpdateCellContent: React.FC<SeverityUpdateCellContentProps> = ({ severity, suggestedSeverity }) =>
  match({ severity, suggestedSeverity })
    .with({ severity: P.not(P.nullish), suggestedSeverity: P.not(P.nullish) }, ({ severity, suggestedSeverity }) => (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <SuggestedSeverity severity={severity} suggestedSeverity={suggestedSeverity} />
      </div>
    ))
    .otherwise(() => <span className={styles.suggestedStatusCell({ status: 'null' })}>-</span>)

export type ScaClassificationCellContentProps = {
  scaResult: ResultsScaResultResponse | undefined
  /** Opens the SCA analysis drawer when the popover Details link is clicked. */
  onDetailsClick?: () => unknown
}

/**
 * Shared content component for SCA classification cell rendering.
 * Used by both outer row ClassificationCell and inner row ScaClassificationCell.
 */
export const ScaClassificationCellContent: React.FC<ScaClassificationCellContentProps> = ({
  scaResult,
  onDetailsClick,
}) =>
  match(scaResult)
    .with({ outcome: { type: 'completed-sca' } }, result => {
      const outcomeType = getOutcomeType({ classification: result.outcome.classification })
      if (outcomeType === undefined) {
        return <span className={styles.suggestedStatusCell({ status: 'null' })}>-</span>
      }
      return <OutcomeTag outcomeType={outcomeType} onDetailsClick={onDetailsClick} />
    })
    .with({ outcome: { type: 'failed-sca' } }, ({ outcome }) => (
      <OutcomeTag
        outcomeType="error"
        errorMessage={outcome.problem.detail || outcome.problem.title || undefined}
        onDetailsClick={onDetailsClick}
      />
    ))
    .with({ outcome: { type: 'blocked-sca' } }, () => (
      <OutcomeTag outcomeType="blocked" onDetailsClick={onDetailsClick} />
    ))
    .with({ outcome: { type: 'excluded-sca' } }, () => (
      <OutcomeTag outcomeType="excluded" onDetailsClick={onDetailsClick} />
    ))
    .with({ outcome: P.optional(undefined) }, () => <InProgressCellIndicator status="first-analysis-in-progress" />)
    .otherwise(() => <span className={styles.suggestedStatusCell({ status: 'null' })}>-</span>)

export type MetadataCellProps = {
  subRowData: SubRowData
  isLatest: boolean
  isFirst: boolean
}

export const MetadataCell: React.FC<MetadataCellProps> = ({ subRowData, isLatest, isFirst }) => {
  const typeLabel = getAnalysisTypeLabel(isFirst)
  const relativeTime = getRelativeTime(subRowData)

  return (
    <div className={expandStyles.metadataCell}>
      <span className={expandStyles.analysisTitle}>{typeLabel}</span>
      {relativeTime && (
        <span className={expandStyles.timestamp}>
          {relativeTime}
          {!isLatest && <ClockCounterClockwiseIcon size={12} weight="light" />}
        </span>
      )}
      {isLatest && <span className={expandStyles.latestBadge}>LATEST</span>}
    </div>
  )
}

// #endregion

// #region Header Components

export const SeverityHeader: React.FC = () => (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
    <span className={styles.header} style={{ margin: 0, padding: 0, width: 'fit-content' }}>
      Severity
    </span>
  </div>
)

export const PixeeSeverityHeader: React.FC<HeaderProps> = ({ environmentData }) => (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
    <img src={environmentData.logoIconHref} height={16} style={{ marginRight: '8px' }} alt="" />
    <span className={styles.header} style={{ margin: 0, padding: 0, width: 'fit-content' }}>
      Severity
    </span>
  </div>
)

type ToolSeverityHeaderProps = {
  tool?: Tool
}

export const ToolSeverityHeader: React.FC<ToolSeverityHeaderProps> = ({ tool }) => (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
    <ToolIcon tool={tool} size="medium" />
    <span className={styles.header} style={{ margin: 0, padding: 0, marginLeft: '8px', width: 'fit-content' }}>
      Severity
    </span>
  </div>
)

export const ScaClassificationHeader: React.FC<HeaderProps> = ({ environmentData }) => (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
    <img src={environmentData.logoIconHref} height={16} style={{ marginRight: '8px' }} alt="" />
    <span
      className={styles.header}
      style={{
        margin: 0,
        marginRight: '8px',
        padding: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 'fit-content',
      }}
    >
      Classification
    </span>
  </div>
)

export const TitleHeader: React.FC = () => (
  <span className={styles.header} style={{ justifyContent: 'flex-start' }}>
    Finding
  </span>
)

export const SuggestedStatusHeader: React.FC<HeaderProps> = ({ environmentData }) => (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
    <img src={environmentData.logoIconHref} height={16} style={{ marginRight: '8px' }} alt="" />
    <span
      className={styles.header}
      style={{
        margin: 0,
        marginRight: '8px',
        padding: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 'fit-content',
      }}
    >
      Suggested Status
    </span>
  </div>
)

export const SeverityUpdateHeader: React.FC<HeaderProps> = ({ environmentData }) => (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
    <img src={environmentData.logoIconHref} height={16} style={{ marginRight: '8px' }} alt="" />
    <span className={styles.header} style={{ margin: 0, marginRight: '8px', padding: 0, width: 'fit-content' }}>
      Severity Update
    </span>
    <SeverityUpdateTooltip environmentData={environmentData} />
  </div>
)

export const FixHeader: React.FC<HeaderProps> = ({ environmentData }) => (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
    <img src={environmentData.logoIconHref} height={16} style={{ marginRight: '8px' }} alt="" />
    <span className={styles.header} style={{ margin: 0, marginRight: '8px', padding: 0, width: 'fit-content' }}>
      Fix
    </span>
  </div>
)

export const ClassificationHeader: React.FC<HeaderProps> = ({ environmentData }) => (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
    <img src={environmentData.logoIconHref} height={16} style={{ marginRight: '8px' }} alt="" />
    <span
      className={styles.header}
      style={{
        margin: 0,
        marginRight: '8px',
        padding: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 'fit-content',
      }}
    >
      Classification
    </span>
  </div>
)

// #endregion

// #region Tooltip Components

type TooltipHeaderProps = {
  environmentData: EnvironmentData
  title: string
}

const TooltipHeader: React.FC<TooltipHeaderProps> = ({ environmentData, title }) => (
  <div
    style={{
      display: 'flex',
      alignItems: 'center',
      marginBottom: '8px',
      justifyContent: 'space-between',
    }}
  >
    <img src={environmentData.logoIconHref} height={24} alt="" />
    <span className={typography.leadBold}>{title}</span>
  </div>
)

type SeverityUpdateTooltipProps = {
  environmentData: EnvironmentData
}

const SeverityUpdateTooltip: React.FC<SeverityUpdateTooltipProps> = ({ environmentData }) => (
  <Tooltip side="bottom">
    <div>
      <TooltipHeader environmentData={environmentData} title="Triage" />
      <p className={typography.small} style={{ margin: 0 }}>
        Based on triage analysis, Pixee may be able to suggest the severity is actually higher or lower than the tool
        initially reported.
      </p>
    </div>
  </Tooltip>
)

// #endregion
